import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import NavHeader from "@/components/NavHeader";
import api from "@/lib/api";
import { STAT_KEYS } from "@/lib/character";
import { Cpu, Skull, Package, Zap } from "lucide-react";

export default function SharedCharacter() {
  const { shareId } = useParams();
  const [character, setCharacter] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/share/${shareId}`);
        setCharacter(r.data.character);
      } catch {
        setError("This link is invalid or expired.");
      }
    })();
  }, [shareId]);

  if (error) {
    return (
      <div className="min-h-screen scanlines">
        <NavHeader />
        <div className="max-w-[900px] mx-auto px-4 sm:px-6 py-16">
          <div className="panel panel-magenta p-8">
            <Skull className="text-[color:var(--accent-magenta)]" />
            <div className="font-display font-black text-2xl uppercase mt-3">DEAD LINK</div>
            <div className="font-mono text-sm text-[color:var(--text-secondary)] mt-2">{error}</div>
          </div>
        </div>
      </div>
    );
  }
  if (!character) {
    return (
      <div className="min-h-screen scanlines">
        <NavHeader />
        <div className="max-w-[900px] mx-auto px-4 sm:px-6 py-16 font-mono text-sm text-[color:var(--text-muted)]">LOADING PUNK…</div>
      </div>
    );
  }

  const list = (arr, label, icon) => (
    <div className="panel p-4">
      <div className="flex items-center gap-2 mb-2">
        {icon} <div className="font-display uppercase font-black tracking-tight text-lg">{label}</div>
        <span className="tape tape-cyan text-[10px] ml-auto">{(arr || []).length}</span>
      </div>
      {(arr || []).length === 0 && <div className="font-mono text-xs text-[color:var(--text-muted)]">—</div>}
      {(arr || []).map((it, i) => (
        <div key={i} className="text-xs font-mono text-[color:var(--text-secondary)] py-1 border-b border-[color:var(--border-hard)] last:border-0">
          {it.text}
        </div>
      ))}
    </div>
  );

  return (
    <div className="min-h-screen scanlines pb-16">
      <NavHeader />
      <main className="max-w-[1200px] mx-auto px-4 sm:px-6 py-6">
        <div className="tape tape-cyan text-[10px] mb-3">READ-ONLY // GM COPY</div>

        <div className="panel p-4 sm:p-6 mb-4">
          <div className="text-[10px] font-mono uppercase tracking-widest text-[color:var(--accent-cyan)]">{character.char_class || "// UNCLASSIFIED"}</div>
          <div className="font-display font-black uppercase tracking-tighter text-4xl sm:text-5xl mt-1">{character.name || "UNNAMED PUNK"}</div>
          <div className="flex flex-wrap gap-4 mt-3 text-xs font-mono uppercase tracking-widest">
            <span className="tape">HP {character.hp_current || 0}/{character.hp_max || 0}</span>
            <span className="tape tape-cyan">CREDS {(character.sats || 0).toLocaleString()}¤</span>
            <span className="tape tape-magenta">DEBT {(character.debt || 0).toLocaleString()}¤</span>
            <span className="tape tape-magenta">GLITCHES {character.glitches_count || 0}/4</span>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-4">
          {STAT_KEYS.map((s) => (
            <div key={s.key} className="panel p-3 text-center">
              <div className="text-[10px] font-mono uppercase text-[color:var(--text-muted)]">{s.abbr}</div>
              <div className="font-display text-3xl font-black text-[color:var(--accent-yellow)]">
                {(character[s.key] || 0) >= 0 ? "+" : ""}{character[s.key] || 0}
              </div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {list(character.weapons, "WEAPONS", <Skull className="text-[color:var(--accent-magenta)]" size={16} />)}
          {list(character.armor, "ARMOR", <Package className="text-[color:var(--accent-cyan)]" size={16} />)}
          {list(character.gear, "GEAR", <Package className="text-[color:var(--accent-yellow)]" size={16} />)}
          {list(character.cybertech, "CYBERTECH", <Cpu className="text-[color:var(--accent-magenta)]" size={16} />)}
          {list(character.apps, "APPS", <Cpu className="text-[color:var(--accent-cyan)]" size={16} />)}
          {list(character.nano_powers, "NANO POWERS", <Zap className="text-[color:var(--accent-cyan)]" size={16} />)}
        </div>

        {[["description","DESCRIPTION"],["bad_habits","BAD HABITS"],["worldly_possessions","WORLDLY POSSESSIONS"],["notes","NOTES"]].map(([k, l]) => (
          character[k] ? (
            <div key={k} className="panel p-4 mt-4">
              <div className="text-[10px] font-mono uppercase tracking-widest text-[color:var(--text-muted)]">{l}</div>
              <div className="font-mono text-sm text-[color:var(--text-secondary)] whitespace-pre-wrap mt-1">{character[k]}</div>
            </div>
          ) : null
        ))}
      </main>
    </div>
  );
}
